const {
  laserEngravingFacts: facts,
} = require('../../lib/laser-engraving-facts')

// Existing PrintAWorld Contentful project assets, verified 2026-09-15.
// Keep original asset URLs; responsive image transforms are applied by the view.
const images = {
  coasters: {
    url: 'https://images.ctfassets.net/dovsnf6qrcem/77AfaURoMS6WKr0wAYrx0e/56e95feb86b9c6eb113b95469fc4a7b4/laser_engraved_coasters.png',
    alt: 'Laser-engraved coasters from PrintAWorld’s project gallery',
    title: 'Custom coasters',
    caption: 'Artwork engraved onto finished objects.',
    width: 1000,
    height: 800,
  },
  metal: {
    url: 'https://images.ctfassets.net/dovsnf6qrcem/5kMuiGZqUnAsEkybHddiPs/c59697fad14eabe621d9f977507f5a31/laser_engraved_stainless_steel.jpg',
    alt: 'Metal frames engraved by PrintAWorld for Nouveau’s Touchless Link',
    title: 'Nouveau · Touchless Link™',
    caption: 'Laser engraving on metal product frames.',
    width: 1280,
    height: 720,
  },
  leather: {
    url: 'https://images.ctfassets.net/dovsnf6qrcem/6smsda2DN5OVG5FAZnoUNc/74b151f4c5016dce619b1519ff5cccd2/L1020859.jpg',
    alt: 'Handmade leather engraved by PrintAWorld',
    title: 'Handmade leather',
    caption: 'A custom mark on a material with its own texture.',
    width: 1920,
    height: 1280,
  },
}

const pricingAnswer = `PrintAWorld’s laser engraving setup is ${facts.setupRange} per job, plus ${facts.pieceRange}: small flat items from $10; bottles, curved or multi-side items up to $35. Rush adds 50%. Item size, engraving size, material, complexity and quantity determine the quote. These are budget ranges, not an hourly rate.`
const timingAnswer = `Standard engraving lead time is ${facts.standardLeadTime}. Some suitable projects can be completed as fast as next day or within a week, depending on scope, availability, and how quickly items reach us. Rush work is confirmed project by project. Include transit time when planning your in-hand date.`

const pages = {
  houston: {
    path: '/services/laser-engraving/texas/houston/',
    city: 'Houston',
    title: 'Laser Engraving Houston | Mail-In Metal, Wood & Leather',
    description:
      'Custom engraving for Houston businesses. Ship approved items to our New York studio. $75–$150 setup plus production; client-paid shipping.',
    eyebrow: 'Houston projects / Engraved in New York',
    heading: 'Laser engraving for Houston gifts, awards, and signs.',
    answer:
      'PrintAWorld offers custom laser engraving for Houston businesses through our New York studio. Send photos and artwork for review, then ship approved items to us. We review metal, wood, leather, and acrylic projects. Setup is $75 plus per-piece production; clients pay shipping to and from the studio.',
    cta: 'Get a Houston project quote',
    hero: 'metal',
    workTitle: 'Engraving examples.',
    materialsTitle: 'Check the material before you ship.',
    introTitle: 'One logo across a batch—or a different name on every piece.',
    intro:
      'Planning client gifts, team awards, or a branded sign for your Houston business? Tell us what stays the same and what changes between items. A repeat logo and a set of individually named pieces need different preparation. We review the artwork, object, and finish before you commit to shipping.',
    projectTitle: 'Plan the batch before you pack it.',
    projectIntro:
      'A useful brief shows us more than the total quantity. These details help us review the job remotely and quote the work you actually need.',
    projectSteps: [
      {
        title: 'Separate the designs',
        body: 'List how many pieces use each logo or name. For awards, match each recipient to the correct item and flag any placement changes.',
      },
      {
        title: 'Show the actual finish',
        body: 'Send photos of the supplied items, including coatings and raised edges. For metal, we test the actual material and finish; a supplier’s material label cannot tell us the final appearance.',
      },
      {
        title: 'Budget for the round trip',
        body: 'Shipping is paid by the client, separately from setup and engraving. Share your Houston delivery ZIP and in-hand date so the shipping plan can be reviewed with production.',
      },
    ],
    logisticsTitle: 'Houston → New York → your delivery address.',
    logistics:
      'We do not have a Houston storefront or local pickup point. After project approval, ship to the New York receiving address we confirm. Clients pay outbound and return shipping. Confirm the return destination and schedule with us before dispatching your items; the production lead time does not include transit.',
    faqTitle: 'Planning an engraving order from Houston.',
    quoteTitle: 'Tell us about your Houston project.',
    quoteIntro:
      'Send the item details, artwork, quantity, Houston delivery ZIP, and the date you need the finished pieces. We’ll review the fit, price, and shipping schedule before you send anything.',
    faqs: [
      {
        question: 'Do you engrave locally in Houston?',
        answer:
          'No. PrintAWorld engraves in New York and serves Houston through shipping. Project review starts online. If you need walk-in service, local pickup, or work on an item that cannot be shipped, a Houston-based shop is a better fit.',
      },
      {
        question: 'What does an engraving order from Houston cost?',
        answer: `${pricingAnswer} Clients also pay shipping to and from our New York studio; shipping is not included in these production ranges.`,
      },
      {
        question: 'Can you personalize a batch of company gifts or awards?',
        answer:
          'Send the quantity, photos, material, engraving placement, and a list of the names or designs that change. We review compatibility and preparation before accepting the project. Show us the actual items rather than assuming every product with the same material label will engrave alike.',
      },
      {
        question: 'How much time should we allow for a shipped order?',
        answer: `${timingAnswer} For Houston orders, allow for both inbound and return shipping. Next-day production, when approved, does not mean next-day delivery in Houston.`,
      },
    ],
  },
  losAngeles: {
    path: '/services/laser-engraving/california/los-angeles/',
    city: 'Los Angeles',
    title: 'Laser Engraving Los Angeles | Mail-In Metal & Wood',
    description:
      'Engraving for Los Angeles agency and brand projects, produced in New York. Review artwork, pricing, and client-paid shipping before sending items.',
    eyebrow: 'Los Angeles projects / Engraved in New York',
    heading: 'Laser engraving for Los Angeles brands and agencies.',
    answer:
      'PrintAWorld handles laser engraving for Los Angeles brands and agencies at our New York studio. We review logos, artwork, and supplied items before you ship. Setup is $75 plus per-piece production, with shipping paid by the client. There is no Los Angeles storefront; approved projects travel to New York for engraving.',
    cta: 'Get an LA project quote',
    hero: 'coasters',
    workTitle: 'Engraving examples.',
    materialsTitle: 'Choose the finish your design needs.',
    introTitle: 'Start with the look your client approved.',
    intro:
      'A logo in a design file does not tell us how it should look on the finished object. For a Los Angeles campaign, launch, or client gift, send the item and a visual reference together. Tell us whether the goal is a subtle material mark, strong contrast, or an opaque black graphic. We’ll help choose between engraving and printing.',
    projectTitle: 'Get the artwork and deadline into the same brief.',
    projectIntro:
      'For an agency project, the person approving the design may not be the person sending the items. Make those details clear before the shipment leaves.',
    projectSteps: [
      {
        title: 'Show the placement',
        body: 'Send the logo or artwork with a marked-up photo, width, and height. Include each version if the campaign uses different names or designs, and identify who will approve the final details.',
      },
      {
        title: 'Decide what “black” means',
        body: 'An engraved mark depends on the material and finish. If the approved design requires fully black or full-color artwork, we usually recommend UV printing instead, subject to item review.',
      },
      {
        title: 'Work back from the delivery date',
        body: 'Give us the date the pieces must arrive in Los Angeles, not only the launch date. Include when the items can leave for New York. Clients pay both shipping legs, and transit is separate from production.',
      },
    ],
    logisticsTitle: 'Your LA brief. Our New York studio.',
    logistics:
      'Review the project with us online before shipping. We’ll confirm the New York receiving address and the return-shipping plan for approved work. Clients pay shipping both ways. There is no LA drop-off location, and rush production does not remove the need to allow for the round trip.',
    faqTitle: 'Before your Los Angeles project ships.',
    quoteTitle: 'What does your LA team need to make?',
    quoteIntro:
      'Send the object, artwork placement, quantity, delivery ZIP, and in-hand date. If the design is still being approved, tell us what is decided and what may change.',
    faqs: [
      {
        question: 'Is PrintAWorld a local Los Angeles engraving shop?',
        answer:
          'No. We serve Los Angeles clients remotely and engrave at our New York studio. Approved items ship to New York and back at the client’s expense. For same-day local pickup or an item you cannot ship, choose a shop near you.',
      },
      {
        question: 'Should we engrave or print a fully black logo?',
        answer:
          'PrintAWorld usually recommends UV printing when the design needs a fully black graphic. Laser engraving produces a material-dependent result; black artwork on screen does not guarantee a black engraved mark. We review the object, finish, and intended use before recommending either process.',
      },
      {
        question: 'What should an agency include in an engraving brief?',
        answer:
          'Include item photos or a product link, material and coating, overall dimensions, artwork width and height, placement, quantity, and any changing names. Add the delivery ZIP, in-hand date, and who will approve the work. A reference image is useful even if final artwork is not ready.',
      },
      {
        question: 'How much does a shipped LA engraving project cost?',
        answer: `${pricingAnswer} Client-paid shipping to and from New York is separate. Tell us about the deadline and shipment before booking a service.`,
      },
      {
        question: 'Can you meet a launch date in Los Angeles?',
        answer: `${timingAnswer} We review the full schedule, including item arrival in New York and return transit to Los Angeles. Do not book shipping on the assumption that a rush slot is available.`,
      },
    ],
  },
  national: {
    path: facts.nationalPath,
    title: 'Custom Laser Engraving Service | Metal, Wood & Leather',
    description:
      'Custom laser engraving for metal, wood, leather, and acrylic. $75–$150 setup plus production. New York fabrication with nationwide shipping.',
    eyebrow: 'Laser engraving / Nationwide service',
    heading: 'Custom laser engraving for products, awards, and signs.',
    answer:
      'Put your logo, names, or artwork on metal, wood, leather, and acrylic with PrintAWorld. We engrave in New York and ship nationwide. Send your item photos and design for a project review; pricing starts with a $75 setup fee, plus per-piece production.',
    cta: 'Get an engraving quote',
    workTitle: 'See what engraving looks like on real projects.',
    materialsTitle: 'Start with the material you want to engrave.',
    faqTitle: 'Before you send your items.',
    quoteTitle: 'What would you like to engrave?',
    quoteIntro:
      'Tell us about the items, design, quantity, and deadline. We’ll review the material and placement, then reply with pricing and next steps. Have a reference photo but no final artwork? Start with that.',
    hero: 'coasters',
    introTitle: 'Your design, on the items you have in mind.',
    intro:
      'Adding a logo to client gifts? Personalizing an award set? Engraving a wood sign? Show us the object and the look you want. We’ll review the material, finish, and artwork placement before you ship, including any testing or artwork preparation the project needs.',
    logisticsTitle: 'Made in New York. Shipped across the U.S.',
    logistics:
      'You can work with us from anywhere in the U.S. Start with a quote request, then ship approved items to the receiving address we confirm. Share your delivery ZIP code and the date you need the finished pieces—not just the event date—so we can account for production and transit.',
    faqs: [
      {
        question: 'Can you engrave items we already own?',
        answer:
          'Yes, we review client-supplied items. Send photos, a product link if available, material and finish details, overall dimensions, and the intended engraving placement. Wait for us to confirm the item and delivery location before shipping. Let us know whether a spare item is available for testing.',
      },
      {
        question: 'How much does custom laser engraving cost?',
        answer: pricingAnswer,
      },
      { question: 'How long does laser engraving take?', answer: timingAnswer },
      {
        question: 'What artwork should we send?',
        answer:
          'Send your vector artwork when available, or the image or reference you have. Include the desired engraving width and height, placement on the item, quantity, and any changing names or designs. We will identify any artwork preparation needed when reviewing the quote.',
      },
    ],
  },
  metal: {
    path: facts.metalPath,
    title: 'Metal Engraving Service | MOPA Laser, 8 × 8 in Area',
    description:
      'Custom metal engraving for logos, names, and artwork. $75–$150 setup plus production. MOPA laser, material testing, and nationwide shipping from New York.',
    eyebrow: 'Metal engraving / Nationwide service',
    heading: 'Custom metal engraving for branded products.',
    answer:
      'Add your logo, names, or artwork to metal products and parts with PrintAWorld’s MOPA laser engraving service, made in New York and shipped nationwide. Send your item photos and design for review; setup is $75 plus production, and the maximum engraving area is 8 × 8 inches.',
    cta: 'Get a metal engraving quote',
    workTitle: 'Metal product engraving for Nouveau.',
    materialsTitle: 'What we need to review your item.',
    faqTitle: 'Metal engraving questions, answered.',
    quoteTitle: 'Get a quote for your metal project.',
    quoteIntro:
      'Send your item details, artwork, quantity, and deadline. We’ll review the material, placement, testing needs, and schedule, then reply with next steps. You do not need to choose the laser settings.',
    hero: 'metal',
    introTitle: 'Tell us how you want the engraving to look.',
    intro:
      'Planning a branded product, client gift, or custom part? Tell us whether you want a subtle mark or strong contrast. We consider all metal types and test the actual alloy, coating, and finish before confirming the engraving approach. A photo of the look you want helps us understand the brief.',
    logisticsTitle: 'Ship your items to us in New York.',
    logistics:
      'Start with an online inquiry, wherever you are in the U.S. Once we’ve reviewed and approved the project, we’ll confirm the receiving location and return-shipping plan. Please wait for that confirmation before sending items. In New York, drop-off and pickup are available by arrangement.',
    faqs: [
      {
        question: 'Which metals can you engrave?',
        answer:
          'PrintAWorld considers all metal types for MOPA laser engraving, including stainless steel, aluminum, brass, and coated metal items. We need to test the actual material and finish to see how the engraving will look. A material name alone does not guarantee a particular color or result.',
      },
      {
        question: 'Can metal engraving be completely black?',
        answer:
          'The appearance of a laser mark depends on the metal, finish, and settings. If your priority is a fully black graphic, PrintAWorld usually recommends UV printing instead. We review the object and intended use to confirm whether printing is suitable.',
      },
      {
        question: 'What is your maximum metal engraving size?',
        answer:
          'PrintAWorld’s maximum metal engraving area is 8 × 8 inches. This describes the area being engraved, not the maximum overall object size. Send the full dimensions and photos of raised edges, recesses, or curved surfaces so we can review access and fit.',
      },
      {
        question: 'How much does metal engraving cost?',
        answer: pricingAnswer,
      },
      {
        question: 'Can you engrave curved metal objects?',
        answer:
          'Send photos, overall dimensions, and the exact placement you need. We review curved surfaces and unusual shapes individually; acceptance depends on whether we can reach and position the engraving area. Overall dimensions and engraving access both matter.',
      },
      {
        question: 'Can you engrave metal items we supply?',
        answer:
          'Yes, we review customer-supplied metal items. Send photos or a product link, the material and coating, full dimensions, and artwork placement. Tell us whether you have a spare for testing. Wait for project approval and the confirmed receiving address before shipping.',
      },
    ],
  },
  nyc: {
    path: facts.path,
    title: 'Engraving NYC | Laser Engraving on Metal, Wood & Leather',
    description:
      'Laser engraving in NYC for metal, wood, leather, and acrylic. $75–$150 setup plus production. Arrange local handoff; ask about project-based rush work.',
    eyebrow: 'Laser engraving / New York City',
    heading: 'Laser engraving in NYC for your next launch or event.',
    answer:
      'Get logos, names, and artwork engraved on metal, wood, leather, or acrylic with PrintAWorld. We review your project online, then arrange drop-off and pickup at the confirmed New York studio. Setup is $75 plus production; standard lead time is 2–3 weeks.',
    cta: 'Get an NYC engraving quote',
    workTitle: 'A closer look at our engraving work.',
    materialsTitle: 'What are you bringing to the studio?',
    faqTitle: 'Planning your NYC engraving project.',
    quoteTitle: 'Tell us what you need—and when.',
    quoteIntro:
      'Send the item details, artwork, quantity, and in-hand date. If the deadline is close, tell us when the items can reach us. We’ll review rush availability before you book a courier or visit the studio.',
    hero: 'coasters',
    introTitle: 'Have an event date? Start with the schedule.',
    intro:
      'Branded gifts for a launch, names on awards, or a custom piece with a fixed deadline: tell us when you need it and when the items will be ready. Some suitable projects can be completed as fast as next day or within a week. Rush work depends on the project, available capacity, and how quickly your items reach us; we confirm it before you arrange delivery.',
    logisticsTitle: 'Plan your New York handoff.',
    logistics:
      'Local drop-off and pickup are arranged by appointment at the studio confirmed for your project. Tell us when your items will be ready and whether you’re bringing them, booking a courier, or shipping them. Please do not arrive or dispatch a delivery until the team confirms the receiving location.',
    faqs: [
      {
        question: 'Where can I drop off items for engraving in NYC?',
        answer:
          'PrintAWorld has studios in Glendale, Queens, and Brooklyn. Drop-off and pickup are arranged by appointment at the location confirmed for your project. Send item details first and wait for the receiving location before visiting or booking a courier.',
      },
      {
        question: 'Do you offer next-day laser engraving in NYC?',
        answer: timingAnswer,
      },
      {
        question: 'How much does laser engraving in NYC cost?',
        answer: pricingAnswer,
      },
      {
        question: 'Can you engrave a metal logo or business card?',
        answer:
          'We review metal logos and card projects using our MOPA laser, with a maximum engraving area of 8 × 8 inches. We test the actual metal and finish first. If the design needs to be fully black or full color, we usually recommend reviewing UV printing instead.',
      },
    ],
  },
}

// Add city cards only when their dedicated engraving page is ready.
// These are service areas, not a registry of physical studios.
const serviceCities = [
  {
    name: 'Houston',
    code: 'HOU',
    mode: 'Ship to our New York studio',
    path: pages.houston.path,
    description:
      'Plan a batch of branded gifts, awards, or signs. Review the items and artwork before shipping; clients pay shipping both ways.',
    cta: 'Plan a Houston engraving project',
  },
  {
    name: 'Los Angeles',
    code: 'LA',
    mode: 'Ship to our New York studio',
    path: pages.losAngeles.path,
    description:
      'Bring your agency or brand brief to our New York team. Review the finish, artwork, and delivery deadline before sending items.',
    cta: 'Plan an LA engraving project',
  },
  {
    name: 'New York City',
    code: 'NYC',
    mode: 'Local drop-off & pickup',
    path: facts.path,
    description:
      'Plan your project with our New York team. Drop-off and pickup are available by appointment at the confirmed Queens or Brooklyn studio.',
    cta: 'Explore laser engraving in NYC',
  },
]

module.exports = { pages, images, pricingAnswer, timingAnswer, serviceCities }
